import {
    Background,
    BackgroundColor,
    Container,
    ContainerType,
    Spacer,
    SpacerVariant,
    backgroundOptions,
    spacerOptionsAll,
} from "../components/layout"
import { Color, TypographyColor } from "../components/typography"
import { Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import FixedWidthSlider from "../components/slider/fixed-width-slider"
import React from "react"
import { TestimonialBenefitProps } from "./TestimonialBenefit"
import { TestimonialCardProps } from "./TestimonialCard"
import classNames from "classnames"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"

interface TestimonialsProps {
    background: { color: BackgroundColor } | BackgroundColor
    headline: TypographyElement[]
    benefitsTitle: TypographyElement[]
    testimonials?: {
        id: string
        props: TestimonialCardProps
        type: "TestimonialCard"
    }[]
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
}

const Testimonials: types.Brick<TestimonialsProps> = ({
    background,
    headline,
    benefitsTitle,
    testimonials,
    spacerTop,
    spacerBottom,
}) => {
    const bgColor = typeof background === "object" ? background.color : background
    const isDark = bgColor === BackgroundColor.Green || bgColor === BackgroundColor.DarkBlumine

    const emptyHeadline = useCheckForEmptyText(headline)
    const emptyBenefitsTitle = useCheckForEmptyText(benefitsTitle)

    return (
        <Background color={bgColor}>
            <Spacer variant={spacerTop} />
            <Container type={ContainerType.Redesign}>
                <div className="sm:grid sm:grid-cols-12 sm:gap-x-3.75 lg:gap-x-5">
                    {!emptyHeadline && (
                        <div
                            className={classNames(
                                "text-left sm:col-span-10 sm:col-start-2 md:col-span-full md:col-start-1 md:text-center",
                                isDark ? "text-white" : "text-dark-grey"
                            )}
                        >
                            <Typography
                                propName="headline"
                                allowList={["S2", "sup", "link"]}
                                className="sm:[&_span]:hyphens-none! md:[&_span]:text-6xl! lg:[&_span]:text-7xl! xl:[&_span]:text-8xl!"
                            />
                        </div>
                    )}
                    <div className="flex flex-col items-start gap-y-5 sm:col-span-10 sm:col-start-2 sm:gap-y-7 md:col-span-full md:col-start-1 md:items-center">
                        {!emptyBenefitsTitle && (
                            <Color color={isDark ? TypographyColor.White : undefined}>
                                <div className="md:text-center">
                                    <Typography
                                        propName="benefitsTitle"
                                        size="P2"
                                        allowList={["sup", "link"]}
                                        className="text-md"
                                    />
                                </div>
                            </Color>
                        )}
                        <Repeater
                            propName="benefits"
                            renderWrapper={children => (
                                <ul className="flex flex-col items-start gap-x-7 gap-y-8 md:flex-row md:items-center md:justify-center lg:gap-x-10">
                                    {children}
                                </ul>
                            )}
                            itemProps={{ color: isDark ? TypographyColor.White : undefined } as TestimonialBenefitProps}
                        />
                    </div>
                </div>
            </Container>
            <div className="mt-10 sm:mt-16">
                <FixedWidthSlider
                    length={testimonials?.length || 0}
                    bgColor={bgColor}
                    containerType={ContainerType.Redesign}
                    hideBullets
                    footnotes={
                        <div
                            className={classNames(
                                "flex flex-col",
                                isDark ? "text-white" : "text-lighter-grey [&_.link:not(:has(sup))]:text-blue"
                            )}
                        >
                            <Repeater propName="smallprint" />
                        </div>
                    }
                >
                    <Repeater propName="testimonials" />
                </FixedWidthSlider>
            </div>
            <Spacer variant={spacerBottom} />
        </Background>
    )
}

Testimonials.schema = {
    name: "Testimonials",
    label: "Testimonials Redesign",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/Testimonials.png`,
    repeaterItems: [
        {
            name: "benefits",
            items: [
                {
                    type: "TestimonialBenefit",
                    label: "Benefit",
                    min: 4,
                    max: 4,
                },
            ],
        },
        {
            name: "testimonials",
            items: [
                {
                    type: "TestimonialCard",
                    label: "Testimonial Card",
                    min: 4,
                },
            ],
        },
        {
            name: "smallprint",
            items: [
                {
                    type: "FootnoteItem",
                    label: "Footnote",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: backgroundOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): TestimonialsProps => ({
        background: { color: BackgroundColor.White },
        spacerTop: SpacerVariant.None,
        spacerBottom: SpacerVariant.None,
        headline: [
            {
                type: "h2",
                children: [{ text: "lorem ipsum dolor sit amet", S2: true }],
            },
        ],
        benefitsTitle: [
            {
                type: "paragraph",
                children: [{ text: "lorem ipsum dolor sit amet:" }],
            },
        ],
    }),
}

export default Testimonials
